<script setup lang="ts">
/**
 * The screen between pressing Deploy and being able to play.
 *
 * It is not decoration for a wait that was already fine. Before this existed the
 * arena opened the instant the socket answered, which is *earlier* than the
 * point at which it is worth looking at: the tank's GLB had not arrived, so it
 * drew as a plain block, and the map's buildings faded in over the next second
 * or two. Both are correct fallbacks during play and both read as a broken game
 * on arrival — the reported symptom was "the tank spawns not full".
 *
 * So the wait is real and the screen covers it honestly. What lifts it is
 * `ArenaScene.whenReady()` — meshes loaded, scenery built, first snapshot in —
 * never a timer. A timer would be wrong in both directions: too short on a cold
 * cache, and needlessly slow once everything is warm.
 */
import { computed, onMounted, ref } from 'vue';
import KeyCap from './KeyCap.vue';
import GearIcon from './GearIcon.vue';
import { wantsTouch } from '@/game/input-source';

const props = defineProps<{
  /** Shown under the title — the map being loaded, when it is known. */
  subtitle?: string;
  /**
   * Whether this is the tutorial, which gets the controls card.
   *
   * **Only the tutorial**, deliberately. Every other sortie is one a player chose
   * from the picker, having already driven a tank; putting a control reference in
   * front of them every time would be the loading screen explaining something
   * they demonstrated they know. This is the one deploy that is somebody's first,
   * and the wait in front of it is the only moment in the game where a player has
   * nothing to do and a reason to read.
   */
  tutorial?: boolean;
}>();

/**
 * The art.
 *
 * Listed rather than globbed so a missing file is a build error rather than an
 * empty array at runtime, and so the set is visible to a reader of this file.
 */
const SCREENS = [
  '/assets/loading/1.webp',
  '/assets/loading/2.webp',
  '/assets/loading/3.webp',
  '/assets/loading/4.webp',
];

/**
 * Picked once, when the component mounts, and never again.
 *
 * A `computed` would re-roll whenever anything above it re-rendered, and the
 * background changing mid-load looks like a glitch rather than variety.
 */
const screen = ref(SCREENS[0]);
onMounted(() => {
  screen.value = SCREENS[Math.floor(Math.random() * SCREENS.length)];
});

const background = computed(() => `url("${screen.value}")`);

/**
 * Which controls to show, read once.
 *
 * A card explaining a mouse to somebody holding a phone is the whole reason this
 * exists: this screen is the only moment in the game where a player has nothing
 * to do and a reason to read, and the first thing a phone player ever read on it
 * was a keyboard diagram.
 *
 * **Read once rather than watched**, unlike `RotatePrompt`'s conjunction: nothing
 * this asks can change while a loading screen is up. `wantsTouch()` is a coarse
 * pointer, no hover and touch hardware — a phone does not become a desktop
 * mid-deploy — and orientation, which *can* change, is not part of it. A fresh
 * component is constructed per deploy, so the answer is never stale.
 */
const touch = ref(false);
onMounted(() => {
  touch.value = wantsTouch();
});
</script>

<template>
  <div class="loading" data-test="loading-screen" :style="{ backgroundImage: background }">
    <div class="scrim"></div>

    <div class="plate">
      <p class="eyebrow">{{ props.tutorial ? 'Basic training' : 'Deploying' }}</p>
      <h1>{{ props.subtitle || (props.tutorial ? 'The drill yard' : 'To the battlefield') }}</h1>
      <div class="bar" aria-hidden="true"><span></span></div>
      <p class="hint">Loading terrain and vehicles…</p>
    </div>

    <section v-if="props.tutorial && touch" class="controls" data-test="loading-controls">
      <div class="group">
        <h2>Drive</h2>
        <svg class="pad-touch" viewBox="0 0 88 48" aria-hidden="true">
          <rect x="1" y="1" width="86" height="46" rx="4" class="edge" />
          <circle cx="17" cy="32" r="9.5" class="edge" />
          <circle cx="17" cy="32" r="3.8" class="lit" />
          <circle cx="71" cy="32" r="7.5" class="edge" />
          <circle cx="71" cy="32" r="2.4" class="lit" />
          <path class="stroke" d="M35 20h18M38.5 17l-3.5 3 3.5 3M49.5 17l3.5 3-3.5 3" />
        </svg>
        <dl>
          <dt>
            <KeyCap cap="Stick forward" kind="touch-stick" wide />
          </dt>
          <dd>Throttle. Pull it back to reverse</dd>
          <dt><KeyCap cap="Stick left or right" kind="touch-stick" wide /></dt>
          <dd>Turn the hull</dd>
        </dl>
      </div>

      <div class="group">
        <h2>Aim and fire</h2>
        <dl>
          <dt><KeyCap cap="Drag anywhere" kind="touch-drag" wide /></dt>
          <dd>Turns the view. The gun follows at its own speed</dd>
          <dt><KeyCap cap="Fire button" kind="touch-fire" wide /></dt>
          <dd>Hold to fire. It says READY when the gun is loaded</dd>
          <dt><KeyCap cap="Two fingers" kind="touch-drag" wide /></dt>
          <dd>Look around without turning the gun</dd>
        </dl>
      </div>

      <p class="aside">
        <GearIcon class="aside-gear" /> menu, settings and music
      </p>
    </section>

    <section v-else-if="props.tutorial" class="controls" data-test="loading-controls">
      <div class="group">
        <h2>Drive</h2>
        <div class="pad" aria-hidden="true">
          <KeyCap cap="W" kind="key" class="pad-w" />
          <KeyCap cap="A" kind="key" class="pad-a" />
          <KeyCap cap="S" kind="key" class="pad-s" />
          <KeyCap cap="D" kind="key" class="pad-d" />
        </div>
        <dl>
          <dt><KeyCap cap="W" kind="key" /><KeyCap cap="S" kind="key" /></dt>
          <dd>Throttle and reverse</dd>
          <dt><KeyCap cap="A" kind="key" /><KeyCap cap="D" kind="key" /></dt>
          <dd>Turn the hull</dd>
        </dl>
      </div>

      <div class="group">
        <h2>Aim and fire</h2>
        <dl>
          <dt><KeyCap cap="Move the mouse" kind="mouse-move" wide /></dt>
          <dd>Turns the view. The gun follows at its own speed</dd>
          <dt>
            <KeyCap cap="Left click" kind="mouse-left" wide />
            <span class="or">or</span>
            <KeyCap cap="Space" kind="key" wide />
          </dt>
          <dd>Fire</dd>
          <dt><KeyCap cap="Hold right" kind="mouse-right" wide /></dt>
          <dd>Look around without turning the gun</dd>
        </dl>
      </div>

      <p class="aside">
        <KeyCap cap="Esc" kind="key" wide /> menu and settings
        <span class="dot">·</span>
        <KeyCap cap="M" kind="key" /> music on and off
      </p>
    </section>
  </div>
</template>

<style scoped>
.loading {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: flex-end;
  background-color: #0a0c10;
  background-size: cover;
  background-position: center;
  /* The cursor is hidden in the arena, and this sits over the arena. Showing an
     arrow here and then taking it away a second later is a flicker. */
  cursor: none;
}

.scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(8, 10, 14, 0.94) 0%, rgba(8, 10, 14, 0.55) 34%, rgba(8, 10, 14, 0.1) 70%);
}

.plate {
  position: relative;
  padding: 0 clamp(1.5rem, 6vw, 6rem) clamp(2.5rem, 8vh, 5rem);
  width: 100%;
  max-width: 46rem;
}

.eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.72rem;
  letter-spacing: 0.34em;
  text-transform: uppercase;
  color: #E8722C;
}

h1 {
  margin: 0 0 1.1rem;
  font-size: clamp(1.6rem, 4vw, 2.6rem);
  line-height: 1.1;
  color: #F2F4F7;
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.75);
}

/* An indeterminate sweep, deliberately. There is no honest percentage here —
   the browser does not report GLB download progress in a form worth trusting,
   and a bar that jumps 0 → 90 → done is a lie with extra steps. */
.bar {
  height: 3px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.14);
  overflow: hidden;
}

.bar span {
  display: block;
  width: 34%;
  height: 100%;
  border-radius: 2px;
  background: linear-gradient(90deg, transparent, #E8722C, transparent);
  animation: sweep 1.35s ease-in-out infinite;
}

@keyframes sweep {
  from { transform: translateX(-110%); }
  to { transform: translateX(400%); }
}

.hint {
  margin: 0.7rem 0 0;
  font-size: 0.82rem;
  color: rgba(242, 244, 247, 0.66);
}

/* Someone who has asked for less motion still needs to know it is working, so
   the sweep becomes a pulse rather than disappearing. */
@media (prefers-reduced-motion: reduce) {
  .bar span {
    width: 100%;
    animation: pulse 1.8s ease-in-out infinite;
    background: #E8722C;
  }
  @keyframes pulse {
    0%, 100% { opacity: 0.35; }
    50% { opacity: 1; }
  }
}

/* ─── The tutorial's controls card ─────────────────────────────────────────── */

.controls {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: auto auto;
  gap: 1.1rem 2.4rem;
  margin-top: 1.6rem;
  padding: 1.1rem 1.4rem;
  /* Its own plate rather than floating on the painting: four different paintings
     cannot all be dark where this lands, which is the same reason the title has a
     scrim behind it. */
  background: rgb(8 10 13 / 72%);
  border: 1px solid var(--steel-700, #1b212a);
  border-radius: 0.5rem;
  backdrop-filter: blur(2px);
}

.controls h2 {
  margin: 0 0 0.6rem;
  font-size: 0.82rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--steel-400, #63748a);
}

/*
 * The cluster, as three columns with W over S.
 *
 * `grid-template-areas` rather than positioning by index, so the shape is legible
 * in the stylesheet as well as on screen — this is the one block whose *layout is
 * the information*, and a reader should be able to see the cross without running
 * it.
 */
.pad {
  display: grid;
  grid-template-areas:
    '. w .'
    'a s d';
  gap: 0.25rem;
  justify-content: start;
  margin-bottom: 0.9rem;
}

.pad-w { grid-area: w; }
.pad-a { grid-area: a; }
.pad-s { grid-area: s; }
.pad-d { grid-area: d; }

/*
 * The touch diagram sits where the cluster does and takes the same room. Its
 * strokes are the HUD's own two weights: the frame and the rings are the dim
 * steel every unlit control on the screen is drawn in, the knob and the shell
 * the accent every live one is.
 */
.pad-touch {
  width: 11rem;
  height: auto;
  margin-bottom: 0.9rem;
  fill: none;
  stroke-width: 1.4;
}

.pad-touch .edge {
  stroke: var(--steel-600, #2a3340);
}

.pad-touch .lit {
  fill: var(--accent, #e8722c);
  stroke: none;
}

.pad-touch .stroke {
  stroke: var(--steel-400, #63748a);
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* Set to the line it sits in, so the aside stays one row. */
.aside-gear {
  width: 0.95rem;
  height: 0.95rem;
  vertical-align: -0.16em;
}

.controls dl {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 0.4rem 0.7rem;
  margin: 0;
}

.controls dt {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.controls dd {
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.35;
  color: var(--steel-200, #b7c3d1);
}

/* `steel-400`, not the HUD's `steel-600`: that value is tuned for a small word
   sitting *inside* a lit panel, and on this card's dark plate it fell to almost
   nothing — which on the one word carrying "these are alternatives" matters. */
.or {
  font-size: 0.62rem;
  letter-spacing: 0.06em;
  color: var(--steel-400, #63748a);
}

.aside {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  padding-top: 0.9rem;
  border-top: 1px solid var(--steel-700, #1b212a);
  font-size: 0.74rem;
  color: var(--steel-400, #63748a);
}

.dot {
  padding: 0 0.35rem;
  color: var(--steel-600, #2a3340);
}

/* Narrow enough that two groups side by side squeeze the descriptions to one
   word per line — they stack instead. */
@media (width <= 46rem) {
  .controls {
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
  }
}

/*
 * ─── A phone held sideways ───────────────────────────────────────────────────
 *
 * Height is the whole budget here, and stacking the groups spends it: measured at
 * 667×375, the touch card came out **511px tall in a 375px viewport**, so its top
 * — the DRIVE heading and the diagram saying where the two controls are — was
 * above the screen and the card had no scrollbar to find it with. That matters
 * more than it would have before, because this card is now the *only* place a
 * phone player is told the whole scheme: the drill panel has room for the control
 * a drill needs and nothing else.
 *
 * So on a short viewport the two groups go back to sitting side by side and the
 * plate gives up the width for it. This block comes after the narrow-width rule
 * on purpose: a landscape phone matches both, and the *height* is the constraint
 * that decides. A short desktop window (1280×660, the tightest shape a laptop
 * browser takes) matches neither and is untouched.
 */
@media (height <= 500px) {
  .plate {
    /* Was `width: 100%`: it is a flex item beside the card, and at full width it
       took the whole row and pushed the card's own column to nothing. */
    width: auto;
    flex: 0 1 19rem;
    padding: 0 1rem clamp(1rem, 4vh, 2rem);
  }

  .controls {
    /* Overrides the stack above — see the note. */
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem 1.1rem;
    flex: 1 1 auto;
    margin: 0 0.7rem 0.7rem 0;
    padding: 0.7rem 0.9rem 0.6rem;
    /* The net, never the design: if a future row makes this too tall again it
       scrolls inside the card rather than off the top of the screen. */
    max-height: calc(100vh - 1.4rem);
    overflow-y: auto;
  }

  .controls h2 {
    margin-bottom: 0.35rem;
    font-size: 0.72rem;
  }

  .controls dl {
    gap: 0.3rem 0.5rem;
  }

  .controls dd {
    font-size: 0.7rem;
    line-height: 1.3;
  }

  .pad,
  .pad-touch {
    margin-bottom: 0.5rem;
  }

  /* The diagram is the most useful thing on the card and the cheapest to shrink:
     it is two circles and an arrow, and it still reads at half a screen wide. */
  .pad-touch {
    width: 7.5rem;
  }

  .aside {
    padding-top: 0.55rem;
    font-size: 0.68rem;
  }
}
</style>
